import type { ReactElement } from 'react';
import type { Control, UseFormRegister, FieldValues, Path } from 'react-hook-form';

/**
 * Generic shape of a form layout document. Mirrors api/src/lib/form-layout-zod.ts.
 * `TName` is the union of all field names a particular form supports
 * (e.g. EntryFormFieldName | ContractFormFieldName).
 */

export interface LayoutFieldEntry<TName extends string = string> {
  name: TName;
  span: number;
  hidden?: boolean | undefined;
}

export interface LayoutGroup<TName extends string = string> {
  id: string;
  title: string;
  collapsible?: boolean | undefined;
  default_open?: boolean | undefined;
  fields: LayoutFieldEntry<TName>[];
}

export interface LayoutDocument<TName extends string = string> {
  version: 1;
  groups: LayoutGroup<TName>[];
}

/**
 * Each form supplies a registry: name → descriptor. Descriptor includes the
 * label (for the editor + a11y), a default span, an optional visibleWhen
 * predicate (for conditional fields), and a `render` function that returns
 * the actual JSX given the form's control / register and a context bag.
 */
export interface FieldDescriptor<TSchema extends FieldValues, TContext = unknown> {
  label: string;
  helpText?: string;
  placeholder?: string;
  /** Default Mantine grid column span (1-12). */
  defaultSpan: number;
  /** When the field's visibility is conditional, returns false to hide. */
  visibleWhen?: (values: Record<string, unknown>) => boolean;
  /** When true, the renderer adds disabled to this field on edit only. */
  lockedOnEdit?: boolean;
  render: (args: {
    name: Path<TSchema>;
    // Allow Control with any TContext / TTransformedValues so a throwaway
    // preview useForm (TTransformedValues inferred to TSchema) interops with
    // the same renderer as the real form drawer.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: Control<TSchema, any, any>;
    register: UseFormRegister<TSchema>;
    descriptor: FieldDescriptor<TSchema, TContext>;
    context: TContext;
    disabled: boolean;
  }) => ReactElement;
}

export type FieldRegistry<TSchema extends FieldValues, TName extends string, TContext = unknown> = Record<
  TName,
  FieldDescriptor<TSchema, TContext>
>;

/**
 * Compute which registered field names are NOT placed by the layout. The
 * renderer drops them into a sticky "Unassigned" group at the bottom so
 * admins can see + place freshly-added fields.
 */
export function findUnassignedFieldNames<TName extends string>(
  layout: LayoutDocument<TName>,
  allNames: readonly TName[],
): TName[] {
  const placed = new Set<string>();
  for (const g of layout.groups) {
    for (const f of g.fields) placed.add(f.name);
  }
  return allNames.filter((n) => !placed.has(n));
}
